import { SingleUser } from 'utils/api/models/users/singleUser';
import CustomInput from 'components/CustomInput/CustomInput';
import { translate } from 'components/Translation';
import React, { useState } from 'react';
import { Caption, CaptionBold } from 'components/Typography/Typography';
import CustomColorPicker from 'components/CustomColorPicker/CustomColorPicker';
import classNames from 'classnames';
import Done from '@material-ui/icons/Done';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/departmentsSubpageStyle';
import { makeStyles } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import CustomDialog from 'components/CustomDialog/CustomDialog';
import { COLORS } from 'views/Organizations/const/colors';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IDialogBodyProps {
	isOpen: boolean;
	hideDialog: () => void;
	managers: SingleUser[];
	createDepartment: (name: string, uiColor: string, selectedManagers: string[]) => void;
}

const AddDepartmentDialog = ({ managers, isOpen, hideDialog, createDepartment }: IDialogBodyProps) => {
	const [departmentName, setDepartmentName] = useState<string>('');
	const [selectedColor, setColor] = useState<string>(COLORS[0]);
	const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
	const classes = useStyles();

	const setManager = (manager: string) => {
		const filteredManagers = selectedManagers.filter((_manager: string) => _manager !== manager);
		if (filteredManagers.length < selectedManagers.length) {
			setSelectedManagers(() => filteredManagers);
		} else {
			setSelectedManagers((managers: Array<string>) => [...managers, manager]);
		}
	};

	const clearDialog = () => {
		setDepartmentName('');
		setSelectedManagers([]);
		setColor(COLORS[0]);
	};

	const cancelClicked = () => {
		hideDialog();
		clearDialog();
	};

	const addDepartment = () => {
		hideDialog();
		createDepartment(departmentName, selectedColor, [...selectedManagers]);
		clearDialog();
	};

	return (
		<CustomDialog
			isOpen={isOpen}
			hideDialog={hideDialog}
			header={translate('departments.new.department')}
			rightSideButtons={[
				{
					text: translate('cancel'),
					onClick: cancelClicked,
					color: 'tertiary-light',
				},
				{
					text: translate('create'),
					onClick: addDepartment,
					color: 'primary-light',
					disabled: departmentName.length <= 0,
					id: 'create-department-button',
				},
			]}
			dialogBody={
				<div className={classes.dialogBody}>
					<CustomInput
						labelText={translate('departments.department.name')}
						formControlProps={{
							fullWidth: true,
						}}
						id="department-name-input"
						inputProps={{
							value: departmentName,
							onChange: (event: React.FormEvent<HTMLInputElement>) =>
								setDepartmentName((event.target as HTMLInputElement).value),
						}}
					/>
					<div className={classes.colorsCaption}>
						<Caption color="secondary-light" disableMargin>
							{translate('departments.department.color')}
						</Caption>
					</div>
					<CustomColorPicker colors={COLORS} selectedColor={selectedColor} onChange={setColor} />
					<div className={classes.managersCaption}>
						<Caption color="secondary-light">
							<>
								{translate('departments.choose.managers.optional')}
								<br />
								{managers.length > 0 && translate('departments.only.managers')}
							</>
						</Caption>
					</div>
					<ConditionalRender
						show={managers.length <= 0}
						onTrue={() => (
							<Caption color="disabled-light">{translate('departments.no.manager.user.type')}</Caption>
						)}
					/>
					<ConditionalRender
						show={managers.length <= 0}
						onTrue={() => (
							<div className={classes.blueBox}>
								<CaptionBold color="brand">{translate('departments.assign.manager')}</CaptionBold>
								<div className={classes.arrow} />
							</div>
						)}
					/>
					<div className={classes.managers}>
						{managers.map((manager: SingleUser, index: number) => (
							<div
								className={classNames({
									[classes.selectedManager]: selectedManagers.indexOf(manager.userId) !== -1,
									[classes.manager]: true,
								})}
								key={index}
								onClick={() => setManager(manager.userId)}
							>
								<div>
									<Caption color="brand" disableMargin>
										{manager.userName}
									</Caption>
								</div>
								<ConditionalRender
									show={selectedManagers.indexOf(manager.userId) !== -1}
									onTrue={() => (
										<Done
											className={classNames({
												[classes.doneIcon]: true,
												[classes.icon]: true,
											})}
										/>
									)}
									onFalse={() => (
										<Add
											className={classNames({
												[classes.addIcon]: true,
												[classes.icon]: true,
											})}
										/>
									)}
								/>
							</div>
						))}
					</div>
				</div>
			}
		/>
	);
};

export default AddDepartmentDialog;
